import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { upgradeFreePlanModalOpen } from '@/components/Modals/UpgradeFreePlanModal';
import { useNavigateToAdvancedEditor } from '@/hooks/navigation';
import { useNavigateToAgentsEditor } from '@/hooks/navigation/useNavigateToAgentsEditor';
import { useFreeVideos } from '@/hooks/useFreeVideos';
import AgentsFilled from '@/images/AgentsFilled.svg';
import ArrowLeft from '@/images/arrows/ArrowLeft.svg';
import ClapperboardPlayFilled from '@/images/ClapperboardPlayFilled.svg';
import Code from '@/images/Code.svg';
import SendFilled from '@/images/SendFilled.svg';
import Translation from '@/images/Translation.svg';
import UserPlusFilled from '@/images/UserPlusFilled.svg';
import { CreateAvatarModal, useOpenCreateAvatarModal } from '@/pages/avatar';
import { ActionButton } from '@/pages/home/components/ActionBar/ActionButton';
import { ScrollLeftButton, ScrollLeftButtonGradient, ScrollRightButton, ScrollRightButtonGradient } from '@/pages/home/components/Common';
import { CreateTranslationModal } from '@/pages/translation/components/CreateTranslationModal';
import { createTranslationModalOpen } from '@/pages/translation/state';
import { useAnalytics } from '@/services/analytics';
import { useFlag } from '@/services/featureFlags';
import { useTemplateGalleryModal } from '@/state/template-gallery/useTemplateGalleryModal';
import { AppRoutes } from '@/utils/types';
import styled from '@emotion/styled';
import { useTheme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { apiKeyModalIsOpen } from '../ApiKey/ApikeyModal';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    outline: none;
    margin-bottom: -16px;
`;

const ItemsContainer = styled.div`
    display: flex;
    position: relative;
    outline: none;
    margin: 16px 0;
`;

const SectionTitle = styled.h1`
    color: ${({ theme }) => theme.colors.black[900]};
    font-size: 24px;
    font-weight: 700;
    letter-spacing: -0.48px;
`;

const ScrollContainer = styled.div`
    display: flex;
    gap: 16px;
    outline: none;
    padding: 10px 10px 30px 10px;
    margin: -10px -10px -30px -10px;

    /* Hide scrollbar for Chrome, Safari and Opera */
    ::-webkit-scrollbar {
        display: none;
    }

    /* Hide scrollbar for IE, Edge and Firefox */
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */

    overflow-x: scroll;
`;

export interface Action {
    id: string;
    color: string;
    icon: string;
    iconColor?: string;
    iconAsImage?: boolean;
    label: string;
    description: string;
    onClick: () => void;
    isNew?: boolean;
}

export const ActionBar = () => {
    const { pathname } = useLocation();

    const theme = useTheme();
    const mixpanel = useAnalytics();
    const navigate = useNavigate();
    const { navigateToAdvancedEditor } = useNavigateToAdvancedEditor();
    const { navigateToAgentEditor } = useNavigateToAgentsEditor();
    const ref = useRef<HTMLDivElement | null>(null);

    const [showRightScroll, setShowRightScroll] = useState<boolean>(false);
    const [showLeftScroll, setShowLeftScroll] = useState<boolean>(false);
    const setApiKeyModalIsOpen = useSetRecoilState(apiKeyModalIsOpen);
    const openCreateAvatarModal = useOpenCreateAvatarModal();
    const setCreateTranslationModalOpen = useSetRecoilState(createTranslationModalOpen);
    const { outOfFreeVideos } = useFreeVideos();
    const setUpgradeFreePlanModalOpen = useSetRecoilState(upgradeFreePlanModalOpen);
    const { open } = useTemplateGalleryModal();
    const isTemplateGalleryEnabled = useFlag('template_gallery_v2', false);
    const campaignsEnabled = useFlag('campaigns_is_enabled', false);

    const createVideoAction = {
        id: 'getting-started-strip-create-ai-video',
        color: theme.colors.primary[900],
        icon: ClapperboardPlayFilled,
        iconAsImage: true,
        label: 'Avatar Video',
        description: 'Engage your audience with lifelike AI Avatar videos',
        onClick: async () => {
            mixpanel?.track('workspace-cta', { event: 'click', id: 'create-studio', section: 'videos', from: 'getting-started-strip' });
            if (outOfFreeVideos) {
                setUpgradeFreePlanModalOpen(true);
                return;
            }

            if (isTemplateGalleryEnabled) {
                open();
                return;
            }

            await navigateToAdvancedEditor({ options: { from: 'Home' }, mixPanelFrom: 'getting-started-strip' });
        },
    };

    const createTranslationAction = {
        id: 'getting-started-strip-translate-video',
        color: theme.colors.success[750],
        icon: Translation,
        iconColor: theme.colors.white,
        label: 'Video Translation',
        description: 'Connect with your audience by using their native language',
        onClick: () => {
            mixpanel?.track('workspace-cta', {
                event: 'click',
                id: 'create-translation',
                section: 'translations',
                from: 'getting-started-strip',
            });
            if (outOfFreeVideos) {
                setUpgradeFreePlanModalOpen(true);
                return;
            }
            setCreateTranslationModalOpen(true);
        },
    };

    const createAgentAction = {
        id: 'getting-started-strip-create-agent',
        color: '#DA496D',
        icon: AgentsFilled,
        iconAsImage: true,
        label: 'Agent',
        description: 'Train a conversational Agent your users will love',
        onClick: () => {
            mixpanel?.track('workspace-cta', { event: 'click', id: 'create-agents', section: 'agents', from: 'getting-started-strip' });
            navigateToAgentEditor({ mixPanelFrom: 'getting-started-strip', from: 'Home' });
        },
    };

    const createAvatarAction = {
        id: 'getting-started-strip-create-avatar',
        color: theme.colors.secondary[900],
        icon: UserPlusFilled,
        iconColor: theme.colors.white,
        label: 'Avatar',
        description: 'Clone yourself to be used inside the D-ID platform.',
        onClick: () => {
            mixpanel?.track('workspace-cta', {
                event: 'click',
                id: 'create-avatar',
                section: 'avatars',
                from: 'getting-started-strip',
            });
            openCreateAvatarModal({
                from: pathname,
            });
        },
    };

    const createCampaignAction = {
        id: 'getting-started-strip-video-campaign',
        color: '#4950FF',
        icon: SendFilled,
        iconAsImage: true,
        label: 'Video Campaign',
        description: 'Personalize video campaigns at scale.',
        onClick: () => {
            mixpanel?.track('workspace-cta', {
                event: 'click',
                id: 'create-campaign',
                section: 'campaigns',
                from: 'getting-started-strip',
            });
            navigate(AppRoutes.CampaignCreate);
        },
    };

    const generateApiAction = {
        id: 'getting-started-strip-generate-api',
        color: theme.colors.black[700],
        icon: Code,
        iconColor: theme.colors.white,
        label: 'Generate API key',
        description: 'Use our API to integrate your applications with D-ID',
        onClick: () => {
            mixpanel?.track('workspace-cta', { event: 'click', id: 'generate-api', section: 'api', from: 'getting-started-strip' });
            setApiKeyModalIsOpen(true);
        },
    };

    const actions: Action[] = [
        createVideoAction,
        createTranslationAction,
        createAgentAction,
        createAvatarAction,
        ...(campaignsEnabled ? [createCampaignAction] : []),
        generateApiAction,
    ];

    const calculateScroll = () => {
        const scrollWidth = ref?.current?.scrollWidth || 0;
        const offsetWidth = ref?.current?.offsetWidth || 0;
        const scrollLeft = ref?.current?.scrollLeft || 0;

        setShowRightScroll(scrollWidth > offsetWidth + scrollLeft);
        setShowLeftScroll(!!ref.current?.scrollLeft);
    };

    useEffect(() => {
        calculateScroll();
        window.addEventListener('resize', calculateScroll);
        ref.current?.addEventListener('scroll', calculateScroll);

        return () => {
            window.removeEventListener('resize', calculateScroll);
            ref.current?.removeEventListener('scroll', calculateScroll);
        };
    }, []);

    const handleScrollRight = () => {
        ref?.current?.scrollBy({ left: 1000, behavior: 'smooth' });
    };

    const handleScrollLeft = () => {
        ref?.current?.scrollBy({ left: -1000, behavior: 'smooth' });
    };

    return (
        <Container id="action-bar-container">
            <SectionTitle id="action-bar-section" data-testid="action-bar-get-started">
                Get started
            </SectionTitle>

            <ItemsContainer>
                {showLeftScroll && (
                    <>
                        <ScrollLeftButtonGradient />
                        <ScrollLeftButton onClick={handleScrollLeft}>
                            <MaskedIcon src={ArrowLeft} size="24px" />
                        </ScrollLeftButton>
                    </>
                )}

                <ScrollContainer ref={ref}>
                    {actions.map(action => (
                        <ActionButton key={action.id} action={action} />
                    ))}
                </ScrollContainer>

                {showRightScroll && (
                    <>
                        <ScrollRightButtonGradient />
                        <ScrollRightButton onClick={handleScrollRight}>
                            <MaskedIcon src={ArrowLeft} size="24px" rotated />
                        </ScrollRightButton>
                    </>
                )}
            </ItemsContainer>
            <CreateTranslationModal />
            <CreateAvatarModal from="workspace-cta" />
        </Container>
    );
};
